/**
 * Wave 16 — R0–R4 release ladder + 72h soak desk.
 * Soft≠Live: paints /ops/reproduction/* only; never invents R4 complete.
 * RES-3 stay deferred until phrase 「授权 RES-3 promote」.
 * Operator copy: no backend flag dumps / raw endpoint jargon.
 */
import { useMemo } from "react";
import { html, Alert, Field, Input, KV } from "./ui.js";
import { useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

function softReproErr(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That release-check action could not be completed.";
  if (/403|forbidden|canary_forbidden|probe_forbidden|read_forbidden/.test(low)) {
    return "Your seat cannot run the soak clock or isolation check. Ask a platform admin.";
  }
  if (/denials_required/.test(low)) {
    return "Isolation check needs denial results from a real probe — nothing was invented.";
  }
  return n.length > 160 ? n.slice(0, 157) + "…" : n;
}

function levelTone(status) {
  if (status === "pass") return "go";
  if (status === "fail") return "bad";
  if (status === "deferred") return "warn";
  return "info";
}

function softLevelLabel(code, status) {
  const name = {
    R0: "Repo",
    R1: "Health",
    R2: "Isolation",
    R3: "Hardware",
    R4: "Soak",
  }[code] || code;
  const st = {
    pass: "pass",
    fail: "fail",
    pending: "waiting",
    deferred: "held",
  }[status] || String(status || "waiting");
  return `${name} · ${st}`;
}

export function ReproductionDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "");
  const caps = (me && me.caps) || [];
  const canMutate =
    role === "superadmin" ||
    (Array.isArray(caps) &&
      (caps.includes("sa_full_access") ||
        caps.includes("sa_ota_admin") ||
        caps.includes("sa_security_admin")));
  const canRead =
    canMutate || role === "admin" || role === "sa_staff";

  const st = useApi(canRead ? "/ops/reproduction/status" : "");
  const act = useAction();
  const envelope = act.envelope;

  const levels = useMemo(() => {
    const raw = st.data && st.data.levels;
    return Array.isArray(raw) ? raw : [];
  }, [st.data]);

  const stories = useMemo(() => {
    const raw = st.data && st.data.stories;
    return Array.isArray(raw) ? raw : [];
  }, [st.data]);

  if (!canRead) {
    return html`<${Alert} tone="bad" title="Release check sealed"
      say="Only Admin / platform seats can read the R0–R4 honesty ladder."/>`;
  }
  if (st.loading && !st.data) {
    return html`<p className="note">Reading release ladder…</p>`;
  }
  if (st.error && !st.data) {
    return html`<${Alert} tone="bad" title="Release check unavailable"
      say=${softReproErr(st.error)}/>`;
  }

  const d = st.data || {};
  const canary = d.canary || {};
  const highest = String(d.highest_pass || "R0");
  const releaseOk = d.release_complete === true;
  const hours = canary.hours_elapsed;
  const soakLabel =
    hours == null
      ? "not started"
      : Number(hours).toFixed(2) + " / 72h";
  const r4 = levels.find((lv) => lv && lv.code === "R4") || {};
  const r4Ev = (r4 && r4.evidence) || {};
  const parity =
    r4Ev.replica_parity_ok === true
      ? "pass"
      : r4Ev.replica_parity_ok === false
        ? "fail"
        : "pending";
  const parityLabel =
    parity === "pass" ? "Copies match" : parity === "fail" ? "Copies disagree" : "Copies waiting";

  const refresh = () => {
    st.reload && st.reload();
  };

  const startCanary = async () => {
    if (!canMutate) {
      toast && toast("Soak clock start is platform admin only", "bad");
      return;
    }
    if (canary.started) {
      const ok = (typeof window !== "undefined")
        && window.confirm(
          "Restarting the soak clock resets the 72h timer. Continue only if intentional.",
        );
      if (!ok) {
        toast && toast("Soak restart cancelled — timer preserved", "info");
        return;
      }
    }
    const out = await act.run("/ops/reproduction/canary/start", {
      method: "POST",
      body: { note: "cmc-wave16-r4-dual-tenant-canary" },
    });
    if (!out) {
      toast && toast(softReproErr(act.error), "bad");
      return;
    }
    toast && toast(
      "Soak clock started · release stays open until 72h + isolation + real hardware proof",
      "warn",
    );
    refresh();
  };

  return html`<div className="reproduction-desk desk-craft" data-cmc-repro="v637">
    <${Alert} tone=${releaseOk ? "go" : "warn"}
      title=${"Release check · highest pass " + highest}
      say=${releaseOk
        ? "Release is marked complete (unexpected until every gate passes)."
        : ("Release not complete · promote still unauthorized · soak " + soakLabel
          + " · " + parityLabel + ". Hardware proof stays evidence-gated.")}/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="chips" style=${{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
      ${levels.map((lv) => html`<span key=${lv.code} className="chip"
        data-tone=${levelTone(lv.status)} data-cmc-repro-level=${lv.code}>
        ${softLevelLabel(lv.code, lv.status)}</span>`)}
      <span className="chip" data-tone=${levelTone(parity === "pending" ? "pending" : parity)}
        data-cmc-repro-parity=${parity}>
        ${parityLabel}</span>
      ${(() => {
        const r3 = levels.find((lv) => lv && lv.code === "R3") || {};
        const sim = (r3.evidence && r3.evidence.sim_observation) || null;
        const label = sim
          ? ("Sim check · board "
            + (sim.board_ok ? "ok" : "no")
            + " · gps "
            + (sim.gps_ok ? "ok" : "no")
            + " · frame "
            + (sim.frame_ok ? "ok" : "no")
            + " · physical pending")
          : "Sim check · waiting";
        return html`<span className="chip" data-tone=${sim ? "go" : "info"}
          data-cmc-repro-sim=${sim ? "1" : "0"}>${label}</span>`;
      })()}
    </div>

    <div className="kv-grid" style=${{ marginBottom: 14 }}>
      ${levels.map((lv) => html`<div key=${"d-" + lv.code} className="note">
        <strong>${softLevelLabel(lv.code, lv.status)}</strong> — ${lv.detail}</div>`)}
    </div>

    <h3 className="desk-craft__h">Stories A–F</h3>
    <ul className="note" style=${{ marginBottom: 14 }}>
      ${stories.map((s) => html`<li key=${s.id}>
        <strong>${s.id}</strong> · ${s.title} · highest ${s.highest} · ${s.status}
        <span className="muted"> — ${s.note}</span>
      </li>`)}
    </ul>

    <h3 className="desk-craft__h">Soak · 72h dual-tenant</h3>
    <p className="note">Soak analysis stays open until the interval and checks pass.
      Copy parity needs at least four matching samples — nothing is invented.
      We never mark soak complete before 72h + isolation. Promote stays off until authorized.</p>
    <div className="btn-row" style=${{ marginTop: 10, flexWrap: "wrap", gap: 8 }}>
      ${canMutate
        ? html`<button type="button" className="btn btn--go btn--sm" onClick=${startCanary}
            disabled=${!!act.busy}>
            ${canary.started ? "Restart soak clock" : "Start 72h soak"}</button>`
        : null}
      <button type="button" className="btn btn--ghost btn--sm" onClick=${refresh}
        data-cmc-repro-reread="1">
        Refresh ladder</button>
    </div>
    ${!canMutate
      ? html`<${Alert} tone="info" title="Read-only ladder"
          say="Admin can read R0–R4 honesty. Soak start and isolation writes stay platform admin."/>`
      : null}
    ${canary.canary_id
      ? html`<${Field} label="Active soak">
          <${Input} value=${String(canary.canary_id)} readOnly/>
        </${Field}>`
      : null}
  </div>`;
}

export default ReproductionDesk;
